import { useEffect, useRef, useCallback } from 'react'
import { auth } from '@/config/firebase'
import { API_CONFIG } from '@/config/api'
import { useAppStore } from '@/stores/appStore'

/**
 * LEGACY — Context Explorer v1 SSE hook. Now connects to the startup-scoped
 * `/v1/startups/{id}/updates/stream`, resolving the active startup the same way
 * the legacy services do. It previously used the unscoped `/v1/updates/stream`
 * mount, which has been removed: without a startup in the path the backend fell
 * back to the caller's uid, so the stream and the upload could silently end up
 * on different tenants. Kept callable
 * because the pre-wire-up onboarding pages (AudioRecordView, TextInputView,
 * DocumentUploadView, GapFillView, ChatPage) still reference it. Do NOT
 * reach for this in new code — use `useUpdatesStream` (startup-scoped).
 * This file is scheduled for extraction into `services/legacy/` in commit 08.
 */

export type LegacyUpdateStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'info'
  | 'warning'

export type LegacyUpdateEntityType =
  | 'document'
  | 'interview'
  | 'artifact'
  | 'context'

/** Firestore Timestamp as serialized in SSE JSON */
export interface FirestoreTimestamp {
  _seconds: number
  _nanoseconds: number
}

/** Payload for document.processing.completed / document.processing.failed events */
export interface DocumentUpdatePayload {
  document: {
    id: string
    status: 'completed' | 'failed'
    type: string
    name: string
  }
  gapAnalysis: {
    status: 'completed' | 'skipped' | 'failed'
    gapsFound: number
    gapIds: string[]
  }
  error?: string
}

/**
 * Legacy SSE envelope. Differs from the new `UpdateEvent` in types/api.ts —
 * the legacy shape carries `eventType`/`status`/`payload` fields that the
 * Context Explorer v1 consumers rely on.
 */
export interface LegacyUpdateEvent {
  type: 'update'
  id: string
  uid?: string
  startupId: string
  entityType: LegacyUpdateEntityType
  entityId: string
  eventType: string
  status: LegacyUpdateStatus
  payload: Record<string, unknown>
  correlationId?: string
  createdAt: FirestoreTimestamp
}

interface LegacyUpdatesStreamOptions {
  entityTypes?: LegacyUpdateEntityType[]
  entityIds?: string[]
  since?: string
  once?: boolean
  enabled?: boolean
  onUpdate?: (event: LegacyUpdateEvent) => void
  onConnected?: (uid: string) => void
  onError?: (error: Event) => void
}

async function getAuthToken(): Promise<string | null> {
  if (!auth?.currentUser) {
    if (import.meta.env.DEV) {
      return 'MOCK_DEV_TOKEN'
    }
    return null
  }

  try {
    return await auth.currentUser.getIdToken()
  } catch (error) {
    console.error('Failed to get auth token for updates stream:', error)
    return null
  }
}

export function useLegacyUpdatesStream({
  entityTypes,
  entityIds,
  since,
  once,
  enabled = true,
  onUpdate,
  onConnected,
  onError,
}: LegacyUpdatesStreamOptions) {
  const sourceRef = useRef<EventSource | null>(null)

  const entityTypesKey = entityTypes?.sort().join(',') ?? ''
  const entityIdsKey = entityIds?.sort().join(',') ?? ''

  const disconnect = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close()
      sourceRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      disconnect()
      return
    }

    let isActive = true

    const connect = async () => {
      const token = await getAuthToken()
      if (!isActive) return

      const baseUrl = API_CONFIG.BASE_URL
      const startupId = useAppStore.getState().startup?.id
      if (!startupId) {
        // Nothing to subscribe to yet. The effect re-runs when `enabled` or the
        // filters change, and the shell sets the active startup during bootstrap.
        return
      }
      const url = new URL(
        `${baseUrl}/v1/startups/${encodeURIComponent(startupId)}/updates/stream`
      )

      if (token) {
        url.searchParams.set('token', token)
      }
      if (entityTypesKey) {
        url.searchParams.set('entityTypes', entityTypesKey)
      }
      if (entityIdsKey) {
        url.searchParams.set('entityIds', entityIdsKey)
      }
      if (since) {
        url.searchParams.set('since', since)
      }
      if (once) {
        url.searchParams.set('once', 'true')
      }

      const source = new EventSource(url.toString())
      sourceRef.current = source

      source.addEventListener('connected', (event) => {
        try {
          const data = JSON.parse(event.data)
          onConnected?.(data.uid ?? '')
        } catch {
          onConnected?.('')
        }
      })

      source.addEventListener('update', (event) => {
        try {
          const data: LegacyUpdateEvent = JSON.parse(event.data)
          onUpdate?.(data)
        } catch (error) {
          console.error('Failed to parse updates stream event:', error)
        }
      })

      source.onerror = (event) => {
        onError?.(event)
      }
    }

    connect()

    return () => {
      isActive = false
      disconnect()
    }
  }, [
    enabled,
    entityTypesKey,
    entityIdsKey,
    since,
    once,
    onUpdate,
    onConnected,
    onError,
    disconnect,
  ])

  return { disconnect }
}
